/**
 * @file ProfileSettingsListFeature.tsx
 * @copyright <Mindly> 2024
 * @fileoverview
 * This component is responsible for rendering the profile settings list feature.
 */

import {
  IconCreditCard,
  IconGift,
  IconLanguage,
  IconLogout,
  IconPaper,
  IconProfileCircle,
  IconQuestion,
  Item,
  ListItems,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Browser } from '@capacitor/browser';
import { useIonAlert } from '@ionic/react';
import { useIntl } from 'react-intl';
import { environment } from '../../enviroments';
import { PrivateRoutes } from '../../routes/navigation';
import { subscriptionsSelector } from '../../redux/subscriptions/selector';
import { SupportedLangsEnum } from '../../shared/enums/supportedLangs.enum';
import { userSelector } from 'src/redux/user/selector';
import { getUserOnBoardingFlowKeySelector } from 'src/redux/onBoardingFlow/selector';
import useIntercomPlugin from 'src/shared/hooks/useIntercomPlugin';

/**
 * Props for the ProfileSettingsListFeature component.
 */
type ProfileSettingsListFeatureProps = {
  /**
   * Function to handle the logout action.
   */
  handleOnLogout: () => Promise<void>;
  /**
   * Function to open the language modal.
   */
  openLanguageModal: () => void;
  /**
   * The current locale.
   */
  locale: string;
};

/**
 * An type for the profile settings group items.
 */
type ProfileSettingsGroupItems = {
  /**
   * The group name.
   */
  group: string;
  /**
   * The group items.
   */
  data: {
    /**
     * The item title.
     */
    title: string;
    /**
     * The item href.
     */
    href?: string;
    /**
     * The item icon.
     */
    icon: React.ReactNode;
    /**
     * The item click handler.
     */
    onClick?: () => Promise<void> | void;
    /**
     * Whether the item is disabled.
     */
    isDisabled?: boolean;
  }[];
};

/**
 * A component for rendering the profile settings list feature.
 * @param {ProfileSettingsListFeatureProps} props - The props for the component.
 * @returns {JSX.Element} The component.
 */
export function ProfileSettingsListFeature({
  handleOnLogout,
  openLanguageModal,
  locale,
}: ProfileSettingsListFeatureProps): JSX.Element {
  const {
    t,
    i18n: { language },
  } = useTranslation(['profile', 'app']);

  const history = useHistory();
  const [present] = useIonAlert();
  const { showMessenger } = useIntercomPlugin();
  const intl = useIntl();

  const user = useSelector(userSelector);
  const subscriptions = useSelector(subscriptionsSelector());
  const onboardingKey = useSelector(getUserOnBoardingFlowKeySelector);

  const menuGroupItems: Array<ProfileSettingsGroupItems | undefined> = useMemo(
    () => [
      {
        group: 'common',
        data: [
          {
            title: t('profile:profile.details.item'),
            icon: <IconProfileCircle className="h-6 w-6" />,
            onClick: () => history.push(PrivateRoutes.PROFILE_INFO),
          },
          {
            title: t('profile:profile.cards.item'),
            icon: <IconCreditCard className="h-6 w-6" />,
            onClick: () => history.push(PrivateRoutes.PROFILE_CARDS),
          },
          {
            title: t('profile:profile.subscription.item'),
            icon: <IconPaper className="h-6 w-6" />,
            onClick: () => history.push(PrivateRoutes.SUBSCRIPTION_CONTROL),
            isDisabled: !subscriptions.data?.length,
          },
        ],
      },
      SupportedLangsEnum.uk === language
        ? {
            group: 'discounts',
            data: [
              {
                title: t('profile:profile.gift_card.item'),
                icon: <IconGift className="h-6 w-6" />,
                onClick: () => {
                  Browser.open({
                    url: `${environment.WEBSITE_URL}/order-gift${user?.email && `?email=${user?.email}`}`,
                    presentationStyle: 'fullscreen',
                    windowName: '_blank',
                  });
                },
              },
            ],
          }
        : undefined,
      {
        group: 'other',
        data: [
          {
            title: t('profile:profile.language.item'),
            icon: <IconLanguage className="h-6 w-6" />,
            onClick: () => openLanguageModal(),
          },
          {
            title: t('profile:profile.support.item'),
            icon: <IconQuestion className="h-6 w-6" />,
            onClick: () => showMessenger(),
          },
        ],
      },
      {
        group: 'logout',
        data: [
          {
            title: t('profile:profile.logout.item'),
            icon: <IconLogout className="h-6 w-6" />,
            onClick: () => {
              present({
                mode: 'ios',
                header: intl.formatMessage({
                  id: 'general.logout.question',
                  defaultMessage: 'Підтвердити вихід',
                }),
                buttons: [
                  {
                    text: intl.formatMessage({
                      id: 'general.logout.btn',
                      defaultMessage: 'Вийти',
                    }),
                    cssClass: 'confirm-delete-button',
                    handler: async () => {
                      await handleOnLogout();
                    },
                  },
                  {
                    text: intl.formatMessage({
                      id: 'general.no',
                      defaultMessage: 'Ні',
                    }),
                  },
                ],
              });
            },
          },
        ],
      },
    ],
    [subscriptions?.data, subscriptions?.data?.length, locale, intl.formatMessage, t, onboardingKey]
  );

  const filteredMenuGroupItems = menuGroupItems.filter((itemsGroup) => !!itemsGroup) as ProfileSettingsGroupItems[];

  return (
    <>
      {filteredMenuGroupItems.map((menuGroupItem) => (
        <ListItems className="mb-4" key={menuGroupItem.group} withBorders>
          {menuGroupItem.data.map((item) => (
            <Item
              key={item.title}
              leftContent={item.icon}
              onClick={item.onClick}
              isDisabled={item?.isDisabled}
              rightContent={item?.isDisabled ? null : undefined}
            >
              <Typography variant={TypographyVariantsEnum.Text16Regular}>{item.title}</Typography>
            </Item>
          ))}
        </ListItems>
      ))}
    </>
  );
}
